import {
  Alert,
  Anchor,
  Card,
  Group,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { Link } from "react-router";
import { EmptyState, LoadingState, PageHeader } from "@how/ui";
import { CrmTabs } from "./CrmTabs";
import {
  CHANNEL_LABELS,
  consentFilterParam,
  consentStateLabel,
  useConsentCoverage,
  type Channel,
  type ChannelCoverage,
  type ConsentState,
} from "./consent-queries";

const percent = (part: number, whole: number) =>
  whole === 0 ? 0 : Math.round((part / whole) * 100);

/**
 * One count, and the way to the people behind it (#112). "796 have not agreed" is a statistic; the list it opens is
 * something a volunteer can take to Sunday. The number is the link, so the labels stay plain text and read as before.
 */
function CountRow({
  channel,
  state,
  value,
  strong = false,
}: {
  readonly channel: Channel;
  readonly state: ConsentState;
  readonly value: number;
  readonly strong?: boolean;
}) {
  const label = consentStateLabel(channel, state);
  return (
    <Group justify="space-between">
      <Text size="sm" {...(strong ? { fw: 600 } : { c: "dimmed" })}>
        {label}
      </Text>
      <Anchor
        component={Link}
        to={`/people?consent=${consentFilterParam({ channel, state })}`}
        size="sm"
        fw={600}
        className="how-numeric"
        aria-label={`Show the ${String(value)} ${value === 1 ? "person" : "people"}: ${CHANNEL_LABELS[channel]}, ${label.toLowerCase()}`}
      >
        {value}
      </Anchor>
    </Group>
  );
}

/**
 * One channel's coverage. The headline is the number the church can act on — how many people it can actually
 * reach — and the two numbers underneath say why the rest are out of reach, which are different problems.
 */
function ChannelCard({ coverage }: { readonly coverage: ChannelCoverage }) {
  const { channel, reachable, notAgreed, noAddress, total } = coverage;
  const share = percent(reachable, total);
  return (
    <Card>
      <Stack gap="sm">
        <div>
          <Title order={4}>{CHANNEL_LABELS[channel]}</Title>
          <Text size="sm" c="dimmed">
            {reachable} of {total} people can be reached
          </Text>
        </div>

        <Progress.Root
          size="lg"
          aria-label={`${CHANNEL_LABELS[channel]}: ${String(share)}% reachable`}
        >
          <Progress.Section value={share} color="teal" />
        </Progress.Root>

        <Stack gap={6}>
          <CountRow channel={channel} state="agreed" value={reachable} strong />
          <CountRow channel={channel} state="not_agreed" value={notAgreed} />
          <CountRow channel={channel} state="no_address" value={noAddress} />
        </Stack>

        {/*
         * The two states are different problems and the card says which is which: one is fixed by asking, the other
         * by collecting a number. A single "unreachable" figure would hide the one a church can act on this week.
         */}
        <Text size="xs" c="dimmed">
          {notAgreed > 0 && noAddress > 0
            ? "Ask the first group; collect details from the second."
            : notAgreed > 0
              ? "These are the people to ask."
              : noAddress > 0
                ? "Asking will not help here — these people need details on file first."
                : "Everyone on file has agreed."}
        </Text>
      </Stack>
    </Card>
  );
}

/** How much of the church each channel can actually reach, and why the rest cannot be. */
export function ConsentPage() {
  const coverage = useConsentCoverage();

  return (
    <>
      <PageHeader title="People" description="Who your church can contact, and on what." />
      <CrmTabs active="consent" />

      {coverage.isPending ? (
        <LoadingState rows={3} />
      ) : coverage.isError ? (
        <Alert color="red" title="Could not work out who can be contacted">
          Try again in a moment.
        </Alert>
      ) : coverage.data[0]?.total === 0 ? (
        <EmptyState
          title="Nobody on file yet"
          description="Once your church has people, this shows how many of them you can contact and on what."
        />
      ) : (
        <Stack gap="lg">
          <Alert variant="light" title="Why this matters before you need it">
            A church can only message someone who has agreed. Closing a gap takes months, not days —
            people agree at a service, on a form, in a conversation — so it is worth knowing the
            number now rather than on the day you want to send something.
          </Alert>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            {coverage.data.map((c) => (
              <ChannelCard key={c.channel} coverage={c} />
            ))}
          </SimpleGrid>

          <Text size="sm" c="dimmed">
            Consent is recorded per person, per channel, with where it came from. Public forms on
            your website write to it when someone ticks the box, and staff can record it on a
            person&apos;s profile.
          </Text>
        </Stack>
      )}
    </>
  );
}
